import React, { useState, useEffect, useCallback } from 'react';
import {
    X, CheckSquare, Award, BookOpen, AlertTriangle,
    XCircle, Download, RefreshCw, BarChart3, TrendingUp,
    Users, AlertCircle, CheckCircle, ChevronRight, Briefcase, UserPlus, Clock, Activity
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
    StatCard,
    ProgressRing,
    BarChart,
    DonutChart,
    FilterBar,
    AIInsightCard,
    DataTable,
    EmployeeDetailModal
} from './index';
import useDrawerResize from './useDrawerResize';
import AnalyticsAIPanel from './AnalyticsAIPanel';
import NotificationSettingsPanel from './NotificationSettingsPanel';

const EmployeesAnalyticsDrawer = ({ isOpen, onClose }) => {
    // State
    const [activeTab, setActiveTab] = useState('onboarding');
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        dateRange: '30',
        teamId: 'all',
        department: 'all',
        position: 'all',
        riskLevel: 'all'
    });

    // ── Resizable drawer ──
    const { drawerWidth, isDragging, dragHandleProps } = useDrawerResize({
        minWidth: 400, maxWidth: 800, defaultWidth: 600,
        storageKey: 'employeeAnalyticsDrawerWidth',
    });
    const [filterOptions, setFilterOptions] = useState({
        teams: [],
        departments: [],
        positions: []
    });

    // Data state
    const [preboardingData, setPreboardingData] = useState(null);
    const [onboardingData, setOnboardingData] = useState(null);
    const [employeeData, setEmployeeData] = useState(null);
    const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);

    // Fetch filter options
    const fetchFilterOptions = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await api.get('/analytics/filters', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setFilterOptions(response.data.filters);
        } catch (error) {
            console.error('Error fetching filter options:', error);
        }
    };

    const handleDownloadCSV = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await api.post('/analytics/report', {
                reportType: 'employees', format: 'csv'
            }, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = 'employee-analytics.csv';
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success('Employee analytics exported');
        } catch { toast.error('Export failed'); }
    };

    // Fetch data based on active tab
    const fetchData = useCallback(async () => {
        if (!isOpen) return;

        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };

            // Build query params
            const params = new URLSearchParams();
            if (filters.dateRange !== 'all') params.append('period', filters.dateRange);
            if (filters.teamId !== 'all') params.append('teamId', filters.teamId);
            if (filters.department !== 'all') params.append('department', filters.department);
            if (filters.riskLevel !== 'all') params.append('riskLevel', filters.riskLevel);

            const queryString = params.toString();
            const requests = [];

            // Determine which endpoints to call based on active tab
            if (activeTab === 'preboarding') {
                requests.push(api.get(`/analytics/preboarding${queryString ? '?' + queryString : ''}`, { headers }));
            } else if (activeTab === 'onboarding') {
                requests.push(api.get(`/analytics/onboarding${queryString ? '?' + queryString : ''}`, { headers }));
            } else if (activeTab === 'employees') {
                requests.push(api.get(`/analytics/employees${queryString ? '?' + queryString : ''}`, { headers }));
            }

            const responses = await Promise.all(requests);

            if (activeTab === 'preboarding') {
                setPreboardingData(responses[0].data.analytics);
            } else if (activeTab === 'onboarding') {
                setOnboardingData(responses[0].data.analytics);
            } else if (activeTab === 'employees') {
                setEmployeeData(responses[0].data);
            }

        } catch (error) {
            console.error('Error fetching analytics:', error);
            toast.error('Failed to load analytics data');
        } finally {
            setLoading(false);
        }
    }, [isOpen, activeTab, filters]);

    useEffect(() => {
        if (isOpen) {
            fetchFilterOptions();
            fetchData();
        }
    }, [isOpen, fetchData]);

    // Format time helper
    const formatTime = (minutes) => {
        if (!minutes) return '-';
        if (minutes < 60) return `${Math.round(minutes)}m`;
        const hours = Math.floor(minutes / 60);
        const mins = Math.round(minutes % 60);
        return `${hours}h ${mins}m`;
    };

    // Reset filters
    const handleResetFilters = () => {
        setFilters({
            dateRange: '30',
            teamId: 'all',
            department: 'all',
            position: 'all',
            riskLevel: 'all'
        });
    };

    // Tabs configuration
    const tabs = [
        { id: 'onboarding', label: 'Onboarding', icon: TrendingUp },
        { id: 'preboarding', label: 'Preboarding', icon: UserPlus },
        { id: 'employees', label: 'Employees', icon: Users }
    ];

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-hidden">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity" onClick={onClose} />
            <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex">
                <div
                    className="transform transition-all ease-in-out duration-500 sm:duration-700 bg-background flex flex-col shadow-xl"
                    style={{ width: `${drawerWidth}px`, maxWidth: '100vw', transition: isDragging ? 'none' : undefined }}
                >
                    {/* ── Drag handle (left edge) ── */}
                    <div {...dragHandleProps} />

                    {/* Header */}
                    <div className="bg-card border-b border-border px-6 py-4 flex items-center justify-between">
                        <div>
                            <h2 className="text-xl font-bold text-foreground">Employee Analytics</h2>
                            <p className="text-sm text-muted-foreground">Insights on onboarding, preboarding, and employee performance</p>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={handleDownloadCSV}
                                className="p-2 text-muted-foreground hover:text-foreground transition-colors"
                                title="Download CSV"
                            >
                                <Download className="w-5 h-5" />
                            </button>
                            <button
                                onClick={fetchData}
                                disabled={loading}
                                className="p-2 text-muted-foreground hover:text-foreground transition-colors"
                                title="Refresh Data"
                            >
                                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                            </button>
                            <button
                                onClick={onClose}
                                className="p-2 text-muted-foreground hover:text-foreground transition-colors"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="bg-card border-b border-border px-6">
                        <div className="flex space-x-8 -mb-px">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`
                                        group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors
                                        ${activeTab === tab.id
                                            ? 'border-primary text-primary'
                                            : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'}
                                    `}
                                >
                                    <tab.icon className={`
                                        -ml-0.5 mr-2 h-5 w-5
                                        ${activeTab === tab.id ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'}
                                    `} />
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="bg-card px-6 py-4 border-b border-border">
                        <FilterBar
                            filters={filters}
                            options={{
                                teams: filterOptions.teams,
                                departments: filterOptions.departments,
                                positions: filterOptions.positions,
                                showDateRange: true,
                                showRiskLevel: activeTab === 'employees'
                            }}
                            onChange={setFilters}
                            onReset={handleResetFilters}
                            onRefresh={fetchData}
                            loading={loading}
                        />
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-6 scrollbar-hide">
                        {loading && !preboardingData && !onboardingData && !employeeData ? (
                            <div className="flex items-center justify-center h-64">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {/* Preboarding Tab */}
                                {activeTab === 'preboarding' && preboardingData && (
                                    <div className="space-y-6">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <StatCard
                                                title="Preboarding Employees"
                                                value={preboardingData.summary?.totalPreboardingEmployees || 0}
                                                subtitle="Currently in preboarding"
                                                icon={UserPlus}
                                                color="amber"
                                            />
                                            <StatCard
                                                title="Task Completion Rate"
                                                value={`${preboardingData.summary?.completionRate || 0}%`}
                                                subtitle={`${preboardingData.summary?.completedTasks || 0} of ${preboardingData.summary?.totalTasks || 0} tasks`}
                                                icon={CheckSquare}
                                                color="green"
                                            />
                                        </div>

                                        {/* Compliance Progress */}
                                        <div className="bg-card rounded-xl border border-border p-6">
                                            <div className="flex items-center justify-between mb-4">
                                                <div>
                                                    <h3 className="font-semibold text-foreground">Preboarding Compliance</h3>
                                                    <p className="text-sm text-muted-foreground">Compliance task completion for preboarding employees</p>
                                                </div>
                                                <ProgressRing
                                                    value={preboardingData.compliance?.completionRate || 0}
                                                    size={80}
                                                    label="Complete"
                                                />
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-border">
                                                <div className="text-center">
                                                    <p className="text-2xl font-bold text-foreground">{preboardingData.compliance?.totalTasks || 0}</p>
                                                    <p className="text-sm text-muted-foreground">Total Tasks</p>
                                                </div>
                                                <div className="text-center">
                                                    <p className="text-2xl font-bold text-green-600">{preboardingData.compliance?.completed || 0}</p>
                                                    <p className="text-sm text-muted-foreground">Completed</p>
                                                </div>
                                                <div className="text-center">
                                                    <p className="text-2xl font-bold text-red-600">{preboardingData.compliance?.overdue || 0}</p>
                                                    <p className="text-sm text-muted-foreground">Overdue</p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Completion by Group */}
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                            {/* By Team */}
                                            <div className="bg-card rounded-xl border border-border p-6">
                                                <h3 className="font-semibold text-foreground mb-4">Completion by Team</h3>
                                                {preboardingData.byTeam && preboardingData.byTeam.length > 0 ? (
                                                    <BarChart
                                                        data={preboardingData.byTeam.slice(0, 5).map(t => ({
                                                            label: t.team_name,
                                                            value: t.completion_rate,
                                                            suffix: '%'
                                                        }))}
                                                        horizontal
                                                        barColor="#f59e0b"
                                                    />
                                                ) : (
                                                    <p className="text-muted-foreground text-sm">No team data available</p>
                                                )}
                                            </div>

                                            {/* By Department */}
                                            <div className="bg-card rounded-xl border border-border p-6">
                                                <h3 className="font-semibold text-foreground mb-4">Completion by Department</h3>
                                                {preboardingData.byDepartment && preboardingData.byDepartment.length > 0 ? (
                                                    <BarChart
                                                        data={preboardingData.byDepartment.slice(0, 5).map(d => ({
                                                            label: d.department,
                                                            value: d.completion_rate,
                                                            suffix: '%'
                                                        }))}
                                                        horizontal
                                                        barColor="#22c55e"
                                                    />
                                                ) : (
                                                    <p className="text-muted-foreground text-sm">No department data available</p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Preboarding Employee Progress Table */}
                                        <div className="bg-card rounded-xl border border-border overflow-hidden">
                                            <div className="p-4 border-b border-border">
                                                <h3 className="font-semibold text-foreground">Preboarding Progress</h3>
                                            </div>
                                            {preboardingData.employeeProgress && preboardingData.employeeProgress.length > 0 ? (
                                                <DataTable
                                                    data={preboardingData.employeeProgress}
                                                    columns={[
                                                        { key: 'name', label: 'Employee', render: (val) => <span className="font-medium text-foreground">{val}</span> },
                                                        { key: 'department', label: 'Dept' },
                                                        {
                                                            key: 'completionRate', label: 'Progress', render: (val, row) => (
                                                                <div className="flex flex-col gap-1 w-24">
                                                                    <div className="flex justify-between text-xs text-muted-foreground">
                                                                        <span>{val}%</span>
                                                                    </div>
                                                                    <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                                                                        <div className="h-full bg-amber-500 rounded-full" style={{ width: `${val}%` }} />
                                                                    </div>
                                                                </div>
                                                            )
                                                        },
                                                        {
                                                            key: 'overdueTasks', label: 'Overdue', render: (val) => (
                                                                <span className={val > 0 ? 'text-red-600 font-semibold' : 'text-muted-foreground'}>
                                                                    {val}
                                                                </span>
                                                            )
                                                        }
                                                    ]}
                                                    searchable
                                                    searchKeys={['name']}
                                                    pageSize={5}
                                                />
                                            ) : (
                                                <div className="p-8 text-center text-muted-foreground">
                                                    <p>No employees currently in preboarding</p>
                                                </div>
                                            )}
                                        </div>

                                        {/* All Employees with Preboarding History */}
                                        <div className="bg-card rounded-xl border border-border overflow-hidden">
                                            <div className="p-4 border-b border-border">
                                                <h3 className="font-semibold text-foreground">Preboarding History</h3>
                                                <p className="text-sm text-muted-foreground">All employees who have completed or are completing preboarding tasks</p>
                                            </div>
                                            {preboardingData.allEmployeesWithPreboarding && preboardingData.allEmployeesWithPreboarding.length > 0 ? (
                                                <DataTable
                                                    data={preboardingData.allEmployeesWithPreboarding}
                                                    columns={[
                                                        { key: 'name', label: 'Employee', render: (val) => <span className="font-medium text-foreground">{val}</span> },
                                                        {
                                                            key: 'status', label: 'Status', render: (val) => (
                                                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${val === 'preboarding' ? 'bg-amber-100 text-amber-700' :
                                                                    val === 'onboarding' ? 'bg-blue-100 text-blue-700' :
                                                                        val === 'active' ? 'bg-green-100 text-green-700' :
                                                                            'bg-muted text-muted-foreground'
                                                                    }`}>{val}</span>
                                                            )
                                                        },
                                                        { key: 'department', label: 'Department' },
                                                        {
                                                            key: 'preboardingProgress', label: 'Preboarding Progress', render: (val, row) => (
                                                                <div className="flex flex-col gap-1 w-28">
                                                                    <div className="flex justify-between text-xs text-muted-foreground">
                                                                        <span>{val}%</span>
                                                                        <span>{row.completedPreboardingTasks}/{row.totalPreboardingTasks}</span>
                                                                    </div>
                                                                    <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                                                                        <div className={`h-full rounded-full ${val === 100 ? 'bg-green-500' : 'bg-amber-500'}`} style={{ width: `${val}%` }} />
                                                                    </div>
                                                                </div>
                                                            )
                                                        },
                                                        { key: 'daysToComplete', label: 'Days to Complete', render: (val) => val ? `${val} days` : 'In Progress' }
                                                    ]}
                                                    searchable
                                                    searchKeys={['name', 'department', 'status']}
                                                    pageSize={5}
                                                />
                                            ) : (
                                                <div className="p-8 text-center text-gray-500">
                                                    <p>No preboarding data available</p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Bottleneck Tasks */}
                                        {preboardingData.bottlenecks && preboardingData.bottlenecks.length > 0 && (
                                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                                                <div className="flex items-start gap-3">
                                                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                                    <div className="flex-1">
                                                        <h4 className="font-semibold text-amber-800">Bottleneck Tasks</h4>
                                                        <div className="grid grid-cols-1 gap-3 mt-3">
                                                            {preboardingData.bottlenecks.slice(0, 3).map(task => (
                                                                <div key={task.id} className="bg-card/60 rounded-lg p-3 flex justify-between items-center">
                                                                    <span className="font-medium text-foreground truncate flex-1">{task.title}</span>
                                                                    <span className="text-sm text-muted-foreground ml-4">{task.completionRate}% completion</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* AI Insights */}
                                        <AIInsightCard
                                            title="Preboarding Insights"
                                            insight={`${preboardingData.summary?.totalPreboardingEmployees || 0} employees are currently in preboarding with a ${preboardingData.summary?.completionRate || 0}% task completion rate. ${preboardingData.summary?.overdueTasks > 0
                                                ? `There are ${preboardingData.summary.overdueTasks} overdue tasks that need attention.`
                                                : 'All preboarding tasks are on track!'
                                                }`}
                                            suggestions={[
                                                "Which preboarding employees are struggling?",
                                                "What's the average time to complete preboarding?",
                                                "Which preboarding tasks have the lowest completion rate?",
                                                "Show me compliance status for preboarding"
                                            ]}
                                            context={{ preboarding: preboardingData, type: 'preboarding' }}
                                        />
                                    </div>
                                )}

                                {/* Onboarding Tab */}
                                {activeTab === 'onboarding' && onboardingData && (
                                    <div className="space-y-6">
                                        <div className="bg-card rounded-xl border border-border p-6">
                                            <h3 className="font-semibold text-foreground mb-1">Avg Onboarding Duration</h3>
                                            <p className="text-3xl font-bold text-primary">
                                                {onboardingData.averageOnboardingDurationDays != null
                                                    ? `${onboardingData.averageOnboardingDurationDays} days`
                                                    : '—'}
                                            </p>
                                        </div>

                                        {/* Employees who completed onboarding */}
                                        <div className="bg-card rounded-xl border border-border overflow-hidden">
                                            <div className="p-4 border-b border-border">
                                                <h3 className="font-semibold text-foreground">Recently Completed</h3>
                                            </div>
                                            {onboardingData.onboardingCompletedList && onboardingData.onboardingCompletedList.length > 0 ? (
                                                <DataTable
                                                    data={onboardingData.onboardingCompletedList}
                                                    columns={[
                                                        { key: 'name', label: 'Employee', render: (val) => <span className="font-medium text-foreground">{val}</span> },
                                                        { key: 'durationDays', label: 'Duration', render: (val) => val ? `${val} days` : '-' },
                                                        { key: 'onboardingCompleteDate', label: 'Completed', render: (val) => val ? new Date(val).toLocaleDateString() : '-' }
                                                    ]}
                                                    searchable
                                                    searchKeys={['name']}
                                                    pageSize={5}
                                                />
                                            ) : (
                                                <div className="p-8 text-center text-muted-foreground">
                                                    <p>No completions in this period</p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Time to Complete */}
                                        <div className="bg-card rounded-xl border border-border overflow-hidden">
                                            <div className="p-4 border-b border-border">
                                                <h3 className="font-semibold text-foreground">Time to Complete Onboarding</h3>
                                                <p className="text-sm text-muted-foreground">Individual employee onboarding progress</p>
                                            </div>
                                            <DataTable
                                                data={onboardingData.timeToComplete || []}
                                                columns={[
                                                    { key: 'name', label: 'Employee', render: (val) => <span className="font-medium text-foreground">{val}</span> },
                                                    {
                                                        key: 'status', label: 'Status', render: (val) => (
                                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${val === 'active' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                                                                }`}>{val}</span>
                                                        )
                                                    },
                                                    {
                                                        key: 'onboarding', label: 'Onboarding', render: (val) => {
                                                            const data = val || { progress: 0, completed: 0, total: 0 };
                                                            return (
                                                                <div className="flex flex-col gap-1 w-24">
                                                                    <div className="flex justify-between text-xs text-muted-foreground">
                                                                        <span>{data.progress}%</span>
                                                                        <span>{data.completed}/{data.total}</span>
                                                                    </div>
                                                                    <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                                                                        <div className="h-full bg-blue-600 rounded-full" style={{ width: `${data.progress}%` }} />
                                                                    </div>
                                                                </div>
                                                            );
                                                        }
                                                    },
                                                    { key: 'daysToComplete', label: 'Days', render: (val) => val ? `${val} days` : '-' }
                                                ]}
                                                searchable
                                                searchKeys={['name']}
                                                pageSize={5}
                                            />
                                        </div>

                                        {/* Completion by Group */}
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                            {/* By Team */}
                                            <div className="bg-card rounded-xl border border-border p-6">
                                                <h3 className="font-semibold text-foreground mb-4">Completion by Team</h3>
                                                {onboardingData.byTeam && onboardingData.byTeam.length > 0 ? (
                                                    <BarChart
                                                        data={onboardingData.byTeam.slice(0, 5).map(t => ({
                                                            label: t.team_name,
                                                            value: t.completion_rate,
                                                            suffix: '%'
                                                        }))}
                                                        horizontal
                                                        barColor="#6366f1"
                                                    />
                                                ) : (
                                                    <p className="text-muted-foreground text-sm">No team data available</p>
                                                )}
                                            </div>

                                            {/* By Department */}
                                            <div className="bg-card rounded-xl border border-border p-6">
                                                <h3 className="font-semibold text-foreground mb-4">Completion by Department</h3>
                                                {onboardingData.byDepartment && onboardingData.byDepartment.length > 0 ? (
                                                    <BarChart
                                                        data={onboardingData.byDepartment.slice(0, 5).map(d => ({
                                                            label: d.department,
                                                            value: d.completion_rate,
                                                            suffix: '%'
                                                        }))}
                                                        horizontal
                                                        barColor="#22c55e"
                                                    />
                                                ) : (
                                                    <p className="text-muted-foreground text-sm">No data available</p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Bottleneck Tasks */}
                                        {onboardingData.bottlenecks && onboardingData.bottlenecks.length > 0 && (
                                            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                                                <div className="flex items-start gap-3">
                                                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                                    <div className="flex-1">
                                                        <h4 className="font-semibold text-amber-800">Bottleneck Tasks</h4>
                                                        <p className="text-sm text-amber-700 mb-4">
                                                            These tasks are slowing down onboarding completion
                                                        </p>
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                            {onboardingData.bottlenecks.slice(0, 4).map(task => (
                                                                <div key={task.id} className="bg-card/60 rounded-lg p-3">
                                                                    <p className="font-medium text-foreground truncate">{task.title}</p>
                                                                    <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                                                                        <span>{task.completionRate}% completion</span>
                                                                        <span className={task.overdue > 0 ? 'text-red-600' : ''}>{task.overdue} overdue</span>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {/* AI Insights */}
                                        <AIInsightCard
                                            title="Onboarding Insights"
                                            insight={`The average onboarding time is ${onboardingData.averageOnboardingDurationDays || 0} days. ${onboardingData.bottlenecks?.length > 0
                                                ? `There are ${onboardingData.bottlenecks.length} bottleneck tasks slowing down the process.`
                                                : 'Onboarding flows are running smoothly.'}`}
                                            suggestions={[
                                                "Show me employees who are stuck in onboarding",
                                                "Which tasks are causing the biggest delays?",
                                                "List recently completed onboardings",
                                                "Compare onboarding time by department"
                                            ]}
                                            context={{ onboarding: onboardingData, type: 'onboarding' }}
                                        />
                                    </div>
                                )}

                                {/* Employees Tab */}
                                {activeTab === 'employees' && employeeData && (
                                    <div className="space-y-6">
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                            <div className="bg-red-50 rounded-xl border border-red-200 p-4">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <AlertTriangle className="w-4 h-4 text-red-500" />
                                                    <span className="text-xs font-semibold text-red-600">High Risk</span>
                                                </div>
                                                <p className="text-xl font-bold text-red-700">{employeeData.summary.highRisk}</p>
                                            </div>
                                            <div className="bg-amber-50 rounded-xl border border-amber-200 p-4">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <AlertCircle className="w-4 h-4 text-amber-500" />
                                                    <span className="text-xs font-semibold text-amber-600">Medium Risk</span>
                                                </div>
                                                <p className="text-xl font-bold text-amber-700">{employeeData.summary.mediumRisk}</p>
                                            </div>
                                            <div className="bg-green-50 rounded-xl border border-green-200 p-4">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <CheckCircle className="w-4 h-4 text-green-500" />
                                                    <span className="text-xs font-semibold text-green-600">Low Risk</span>
                                                </div>
                                                <p className="text-xl font-bold text-green-700">{employeeData.summary.lowRisk}</p>
                                            </div>
                                        </div>

                                        <div className="bg-card rounded-xl border border-border overflow-hidden">
                                            <div className="p-4 border-b border-border">
                                                <h3 className="font-semibold text-foreground">Employee Performance</h3>
                                            </div>
                                            <DataTable
                                                data={employeeData.employees}
                                                columns={[
                                                    {
                                                        key: 'name', label: 'Name', render: (val, row) => (
                                                            <div className="flex flex-col">
                                                                <span className="font-medium text-foreground">{val}</span>
                                                                <span className="text-xs text-muted-foreground">{row.position}</span>
                                                            </div>
                                                        )
                                                    },
                                                    {
                                                        key: 'metrics.onboarding', label: 'Onboarding', render: (_, row) => {
                                                            const data = row.metrics?.onboarding || { progress: 0 };
                                                            return (
                                                                <span className={`font-medium ${data.progress >= 80 ? 'text-green-600' : 'text-amber-600'}`}>
                                                                    {data.progress}%
                                                                </span>
                                                            );
                                                        }
                                                    },
                                                    {
                                                        key: 'metrics', label: 'Overdue', render: (val) => (
                                                            <span className={val.overdueTasks > 0 ? 'text-red-600 font-bold' : 'text-muted-foreground'}>
                                                                {val.overdueTasks}
                                                            </span>
                                                        )
                                                    },
                                                    {
                                                        key: 'engagementScore', label: 'Engage', render: (val) => (
                                                            <span className={`font-medium ${val >= 70 ? 'text-green-600' : val >= 40 ? 'text-amber-600' : 'text-red-600'}`}>
                                                                {val}%
                                                            </span>
                                                        )
                                                    },
                                                    {
                                                        key: 'actions', label: '', render: (_, row) => (
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSelectedEmployeeId(row.id);
                                                                }}
                                                                className="text-primary hover:text-primary-hover"
                                                            >
                                                                <ChevronRight className="w-4 h-4" />
                                                            </button>
                                                        )
                                                    }
                                                ]}
                                                onRowClick={(row) => setSelectedEmployeeId(row.id)}
                                                searchable
                                                searchKeys={['name', 'email']}
                                                pageSize={10}
                                            />
                                        </div>


                                        {/* AI Insights */}
                                        <AIInsightCard
                                            title="Employee Insights"
                                            insight={`${employeeData.summary?.highRisk || 0} employees are flagged as high risk. ${employeeData.summary?.lowRisk || 0} employees are performing well with low risk scores.`}
                                            suggestions={[
                                                "Who has the lowest engagement score?",
                                                "Show me high risk employees and why",
                                                "Which department has the best performance?",
                                                "Identify employees with overdue tasks"
                                            ]}
                                            context={{ employees: employeeData, type: 'employees' }}
                                        />

                                        {/* AI Chat Panel */}
                                        <AnalyticsAIPanel
                                            analyticsContext={{
                                                type: 'employees',
                                                title: 'Employee Analytics',
                                                data: employeeData,
                                            }}
                                        />

                                        {/* Notification Settings */}
                                        <NotificationSettingsPanel
                                            analyticsContext={{
                                                type: 'employees',
                                                title: 'Employee Analytics',
                                                data: employeeData,
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Employee Detail Modal */}
                    {
                        selectedEmployeeId && (
                            <EmployeeDetailModal
                                employeeId={selectedEmployeeId}
                                onClose={() => setSelectedEmployeeId(null)}
                            />
                        )
                    }

                </div >
            </div >
        </div >
    );
};

export default EmployeesAnalyticsDrawer;
